import { makeAutoObservable } from 'mobx';
import {
	deleteFaceMemoryPhoto,
	postFaceMemoryPhoto,
} from '../../services/apis/FaceMemoryPhotoApi';
import { editImage, faceDetection } from '../../services/apis/BirthdayApi';

import { waitUntil } from '../../utils/WaitUntil';
import { queryClient } from '../../hooks/queries/queryClient';

class MetaverseStore {
	rootStore;

	isConnect = false;
	isStart = false;

	downloadProgress = 0;

	defaultIndex = -1;

	seekPercentage = null;

	selectPhotoType = 'memory';
	firstPhotos = [];
	memoryPhotos = [];
	newPhotos = [];
	wholePhoto = null;

	processedImages = [];

	profileUsers = [];

	constructor(root) {
		makeAutoObservable(this);
		this.rootStore = root;

		this.isConnect = false;
		this.isStart = false;

		this.downloadProgress = 0;

		this.defaultIndex = -1;

		this.seekPercentage = 0;

		this.selectPhotoType = 'memory';
		this.firstPhotos = [];
		this.memoryPhotos = [];
		this.newPhotos = [];
		this.wholePhoto = null;

		this.processedImages = [];

		this.profileUsers = [];
	}

	init(photos) {
		this.memoryPhotos = photos;
		this.rootStore.mqttStore.playerMqtt.setFacePhotos(
			this.setPhotos.bind(this, 'updated'),
			this.setWholePhoto.bind(this),
		);
		this.rootStore.mqttStore.playerMqtt.requestFacePhoto('memory');
	}

	setIsStart(value = !this.isStart) {
		this.isStart = value;
	}

	getIsReady() {
		return (
			this.isConnect && !this.isStart && this.rootStore.likuStore.thisLiku?.uuid
		);
	}

	setIsConnect(data) {
		console.log('[MetaverseConnect] setIsConnect', {
			previous: this.isConnect,
			next: data,
			uuid: this.rootStore.likuStore.thisLiku?.uuid,
		});
		this.isConnect = data;
	}

	async checkLikuConnect() {
		const liku = this.rootStore.likuStore.thisLiku;
		const requestId = `metaverse-${Date.now()}-${Math.random()}`;
		console.log('[MetaverseConnect] check entered', {
			requestId,
			uuid: liku?.uuid,
			language: liku?.language,
			isConnect: this.isConnect,
		});

		if (!liku) {
			console.log('[MetaverseConnect] skipped: no robot', requestId);
			return;
		}

		console.log('[MetaverseConnect] waiting for language', requestId);
		await waitUntil(() => !!this.rootStore.likuStore.thisLiku?.language, 100);

		const language = this.rootStore.likuStore.thisLiku.language;
		console.log('[MetaverseConnect] language available', {
			requestId,
			language,
		});

		if (language !== 'ko') {
			console.log('[MetaverseConnect] requesting Korean', requestId);
			await new Promise((resolve) => {
				this.rootStore.mqttStore.likuMqtt.setLanguage('ko', (...args) => {
					console.log('[MetaverseConnect] language callback', requestId);
					resolve(...args);
				});
			});
		}

		console.log('[MetaverseConnect] requesting contents connection', requestId);
		this.rootStore.mqttStore.playerMqtt.setContentsConnect(() => {
			console.log('[MetaverseConnect] contents connection callback', {
				requestId,
				requestedUuid: liku.uuid,
				currentUuid: this.rootStore.likuStore.thisLiku?.uuid,
			});
			this.setIsConnect(true);
		});
	}

	setDefaultIndex(value = this.defaultIndex + 1) {
		this.defaultIndex = value;
	}

	setDownloadProgress(value) {
		this.downloadProgress = value;
	}

	setSeekPercentage(percentage) {
		this.seekPercentage = percentage;
	}

	toggleSelectPhotoType(value) {
		this.selectPhotoType = value;
		this.profileUsers = []; // 타입 변경 시 프로필 선택 초기화
	}

	setPhotos(type, photos) {
		const newPhotos = this.findNewPhotos(photos);
		if (type === 'memory') {
			this.setMemoryPhotos([...this.memoryPhotos, ...newPhotos]);
		} else {
			// type === 'new' || 'updated'
			this.setNewPhotos([...this.newPhotos, ...newPhotos]);
		}
	}

	getInitialPhotos() {
		const liku = this.rootStore.likuStore.thisLiku?.serial;
		if (!liku) return [];
		const cached = queryClient.getQueryData(['liku-face-memorty', liku]);
		return cached ?? [];
	}

	setMemoryPhotos(photos) {
		this.memoryPhotos = photos;
	}

	setNewPhotos(photos) {
		this.newPhotos = photos;
	}

	takePhoto() {
		this.rootStore.mqttStore.playerMqtt?.requestFacePhoto('new');
	}

	setWholePhoto(photo) {
		if (photo !== null) {
			if (this.wholePhoto) URL.revokeObjectURL(this.wholePhoto);

			this.wholePhoto = URL.createObjectURL(
				new Blob([photo], { type: 'image/png' }),
			);

			this.setPhotos(
				this.selectPhotoType,
				this.memoryPhotos.concat(this.newPhotos),
			);
		}
	}

	getSelectPhots() {
		return this.selectPhotoType === 'memory'
			? this.memoryPhotos
			: this.newPhotos;
	}

	findNewPhotos(updatedPhotos) {
		const initialPhotos = [
			...this.getInitialPhotos(),
			...this.memoryPhotos,
			...this.newPhotos,
		];
		return (
			updatedPhotos?.filter(
				(photo) => !initialPhotos?.some((item) => item.id === photo.id),
			) || []
		);
	}

	async deletePhoto(type, facePhoto) {
		const liku = this.rootStore.likuStore.thisLiku?.serial;
		let index = -1;

		if (type === 'memory') {
			index = this.memoryPhotos.findIndex((p) => p.path === facePhoto.path);
			if (index !== -1) this.memoryPhotos.splice(index, 1);
		} else if (type === 'new') {
			index = this.newPhotos.findIndex((p) => p.path === facePhoto.path);
			if (index !== -1) this.newPhotos.splice(index, 1);
		}

		if (!('bucket' in facePhoto)) {
			await deleteFaceMemoryPhoto(liku, facePhoto.id);
		}
	}

	toggleProfileUser(user) {
		const idx = this.profileUsers.findIndex((pu) => pu.faceId === user.faceId);
		if (idx !== -1) {
			this.profileUsers.splice(idx, 1);
		} else {
			this.profileUsers.push(user);
		}
	}

	isChildSelected(faceId) {
		return this.profileUsers.some((pu) => pu.faceId === faceId);
	}

	updatePhotosFromProfileUsers() {
		const photos =
			this.selectPhotoType === 'memory'
				? [...this.memoryPhotos]
				: [...this.newPhotos];

		const selectedPhotos = photos.filter((p) => p.bucket);

		const added = this.profileUsers.filter(
			(pu) => !selectedPhotos.some((photo) => photo.path === pu.path),
		);
		const removed = selectedPhotos.filter(
			(photo) => !this.profileUsers.some((pu) => pu.path === photo.path),
		);

		added.forEach((item) => {
			const cloned = JSON.parse(JSON.stringify(item));
			delete cloned.faceUser?.name;
			photos.push(cloned);
		});

		removed.forEach((item) => {
			const idx = photos.findIndex((p) => p.id === item.id);
			if (idx !== -1) photos.splice(idx, 1);
		});

		if (this.selectPhotoType === 'memory') {
			this.setMemoryPhotos(photos);
		} else {
			this.setNewPhotos(photos);
		}
	}

	async handleCompleteSelection() {
		const selectedList = this.getSelectPhots();

		console.log('selected photo size : ', selectedList.length);

		const blobs = await Promise.all(
			selectedList.map((photo) => this.convertKidImageUrlToFile(photo)),
		);

		if (blobs.some((blob) => !blob)) {
			console.log('blobs error ');
			return false;
		} else {
			console.log('blobs success ');
		}

		const nextUrls = blobs.map((blob) => URL.createObjectURL(blob));

		console.log('next url size : ', nextUrls.length);

		this.processedImages?.forEach((url) => URL.revokeObjectURL(url));
		this.processedImages = nextUrls;

		console.log('result images size : ', this.processedImages.length);

		await this.applyToDB();
		return true;
	}

	async applyToDB() {
		const liku = this.rootStore.likuStore.thisLiku?.serial;
		if (!liku) return;

		const isMemory = this.selectPhotoType === 'memory';

		const sourcePhotos = isMemory ? this.newPhotos : this.memoryPhotos;
		const targetPhotos = isMemory ? this.memoryPhotos : this.newPhotos;
		const initialPhotos = this.getInitialPhotos();

		await Promise.all(
			sourcePhotos
				.filter((photo) => !('bucket' in photo))
				.map((photo) => deleteFaceMemoryPhoto(liku, photo.id)),
		);

		const addedValues = targetPhotos.filter(
			(photo) => !initialPhotos.some((item) => item.path === photo.path),
		);

		await Promise.all(
			addedValues
				.filter((photo) => 'bucket' in photo)
				.map(async (photo) => {
					try {
						const blob = await this.uploadFromDirectFetch(photo);
						const formData = new FormData();
						formData.append('file', blob);
						return await postFaceMemoryPhoto(liku, formData);
					} catch (err) {
						console.error('Failed to upload photo:', err);
					}
				}),
		);
	}

	async uploadFromDirectFetch(photo) {
		try {
			const response = await fetch(photo.path, {
				method: 'GET',
				mode: 'cors',
				cache: 'no-cache',
			});

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: ${response.statusText}`);
			}

			const blob = await response.blob();
			console.log('original blob size:', blob.size);

			// 크기 조절 함수 호출
			const resizedBlob = await this.resizeToMaxSize(blob);

			console.log('resized blob size:', resizedBlob.size);

			return resizedBlob;
		} catch (error) {
			console.error('Direct fetch failed:', error.message);
			return false;
		}
	}

	resizeToMaxSize(blob, maxBytes = 27880, minQuality = 0.1) {
		return new Promise((resolve, reject) => {
			const imageURL = URL.createObjectURL(blob);
			const image = new Image();

			image.onload = async () => {
				try {
					URL.revokeObjectURL(imageURL);

					const canvas = document.createElement('canvas');
					canvas.width = image.width;
					canvas.height = image.height;
					const ctx = canvas.getContext('2d');

					let quality = 0.95;
					let resizedBlob = null;

					while (quality >= minQuality) {
						ctx.clearRect(0, 0, canvas.width, canvas.height);
						ctx.drawImage(image, 0, 0);

						resizedBlob = await new Promise((res) =>
							canvas.toBlob(res, 'image/jpeg', quality),
						);

						if (resizedBlob.size <= maxBytes) {
							resolve(resizedBlob);
							return;
						}
						quality -= 0.05;
					}

					// 최소 품질 이하인데도 크기 크면 최소 품질 Blob 반환
					resolve(resizedBlob);
				} catch (error) {
					reject(error);
				}
			};

			image.onerror = (err) => {
				URL.revokeObjectURL(imageURL);
				reject(err);
			};

			image.src = imageURL;
		});
	}

	async convertKidImageUrlToFile(photo) {
		try {
			let file;
			if (photo.path.includes('s3')) {
				const blob = await this.uploadFromDirectFetch(photo);
				if (!blob) throw new Error('이미지 다운로드 실패');
				file = new File([blob], 'faceImage.png', { type: 'image/png' });
			} else {
				const response = await fetch(photo.path);
				const blob = await response.blob();
				file = new File([blob], 'faceImage.png', { type: 'image/png' });
			}

			const { data: faces } = await faceDetection(file);
			if (!faces || faces.length === 0) throw new Error('No faces detected');

			const biggestFace = faces.reduce((prev, current) => {
				const prevSize = prev.BoundingBox.Width * prev.BoundingBox.Height;
				const currSize = current.BoundingBox.Width * current.BoundingBox.Height;
				return currSize > prevSize ? current : prev;
			}, faces[0]);

			const imageURL = URL.createObjectURL(file);
			console.log('imageURL', imageURL);
			const res = await editImage(imageURL, biggestFace);
			return res;
		} catch (error) {
			console.error('convertKidImageUrlToFile error:', error);
			return null;
		}
	}

	clear(isAll = true) {
		console.trace('[MetaverseConnect] clear', {
			isAll,
			isConnect: this.isConnect,
			isStart: this.isStart,
			uuid: this.rootStore.likuStore.thisLiku?.uuid,
		});
		this.rootStore.mqttStore.playerMqtt?.unsetMetaverse();

		if (isAll) this.isConnect = false;
		this.isStart = false;

		this.downloadProgress = 0;

		this.defaultIndex = -1;

		this.seekPercentage = 0;

		this.selectPhotoType = 'memory';
		this.firstPhotos = [];
		this.memoryPhotos = [];
		this.newPhotos = [];
		this.wholePhoto = null;

		this.processedImages = [];

		this.profileUsers = [];
	}
}

export default MetaverseStore;
